/* eslint-disable react/jsx-props-no-spreading */

import { HTMLAttributes, ReactNode } from 'preact/compat';
import { route } from 'preact-router';
import { useDispatch } from 'react-redux';

import type { ProductPromptType } from '@/components/products/ProductsPromptModal/redux/types';
import useOpenSpeedyFlow from '@/components/SpeedyFlow/hooks/useOpenSpeedyFlow';
import { isEmbedded, loadPage } from '@/helpers';
import { cn } from '@/lib/utils';
import { useSelector } from '@/redux/helper';
import { createSelectCanAccessHabitat } from '@/redux/selectors/users';

import { buildHabitatURL } from './utils';

export interface HabitatLinkProps extends Omit<HTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> {
  habitatId: string;
  slug: string;
  zooSlug: string;
  query?: string;
  /** The offer to show when the habitat is locked. Same closed vocabulary as the URL's `ppt`. */
  ppt?: ProductPromptType;
  children: ReactNode | ((props: { restricted: boolean }) => ReactNode);
}

type RestrictedHabitatLinkProps = Omit<HabitatLinkProps, 'children'> & {
  children: ReactNode;
};
type AccessibleHabitatLinkProps = Omit<RestrictedHabitatLinkProps, 'habitatId' | 'productsPromptModalState'>;

const AccessibleHabitatLink = ({
  slug,
  zooSlug,
  query,
  children,
  className,
  onClick,
  ...rest
}: AccessibleHabitatLinkProps) => (
  <a
    href={buildHabitatURL(zooSlug, slug, query)}
    data-native={isEmbedded() || undefined}
    className={cn('preflight preflight-a', className)}
    {...rest}
    onClick={(e) => {
      onClick?.(e);
      if (e.defaultPrevented) {
        return;
      }
      if (isEmbedded()) {
        e.preventDefault();
        e.stopPropagation();
        loadPage(buildHabitatURL(zooSlug, slug, query));
      }
    }}
  >
    {children}
  </a>
);

const RestrictedHabitatLink = ({
  habitatId,
  slug,
  zooSlug,
  query,
  children,
  onClick,
  ppt,
  ...rest
}: RestrictedHabitatLinkProps) => {
  const dispatch = useDispatch();
  const openSpeedyFlow = useOpenSpeedyFlow();
  const logged = useSelector((state) => state.user.logged);
  return (
    <AccessibleHabitatLink
      slug={slug}
      zooSlug={zooSlug}
      query={query}
      {...rest}
      data-native={logged || isEmbedded() || undefined}
      onClick={(e) => {
        if (isEmbedded()) {
          onClick?.(e);
          return;
        }
        if (logged) {
          e.preventDefault();
          // They were trying to go to the habitat, so take them there once they can get in. The
          // click is suppressed rather than deferred, so without this a visitor who buys access
          // ends up back where they started and has to find the link again.
          openSpeedyFlow({ kind: 'habitat-access', habitatId, ppt, entryPoint: 'habitat-link' }, () =>
            route(buildHabitatURL(zooSlug, slug, query)),
          );
        }
        onClick?.(e);
      }}
    >
      {children}
    </AccessibleHabitatLink>
  );
};

const HabitatLink = ({ habitatId, children, ppt, ...rest }: HabitatLinkProps) => {
  const canAccessHabitat = useSelector(createSelectCanAccessHabitat(habitatId));
  return canAccessHabitat ? (
    <AccessibleHabitatLink {...rest}>
      {typeof children === 'function' ? children({ restricted: false }) : children}
    </AccessibleHabitatLink>
  ) : (
    <RestrictedHabitatLink habitatId={habitatId} ppt={ppt} {...rest}>
      {typeof children === 'function' ? children({ restricted: true }) : children}
    </RestrictedHabitatLink>
  );
};

export default HabitatLink;
